/**
 * Chart.js loss/accuracy chart.
 *
 * Loss always owns the left axis: the spike after a growth event is the lesson
 * the demo teaches, so it is never hidden behind a control. The metric series on
 * the right axis is the part the user can reconfigure.
 */
import { Chart } from 'chart.js/auto';
import type { DisplayMode, MetricName, SeriesOptions, XAxisMode } from './metric-series.ts';
import { METRIC_LABELS, buildSeries } from './metric-series.ts';
import type { MetricPoint } from './snapshots.ts';

export interface ChartSelection {
  xAxis: XAxisMode;
  metric: MetricName;
  display: DisplayMode;
  /** Trailing x-axis units to show, or `null` for the full history. */
  span: number | null;
}

export interface MetricsChartOptions {
  /**
   * Hard cap on plotted points. Guards the tab against a multi-minute session
   * with a smoothing-free curve, independent of the "All" span.
   */
  window?: number;
}

export class MetricsChart {
  #chart: Chart<'line'>;
  #window: number;
  #points: readonly MetricPoint[] = [];
  #selection: ChartSelection = {
    xAxis: 'batch',
    metric: 'accuracy',
    display: 'raw',
    span: null,
  };

  constructor(canvas: HTMLCanvasElement, options: MetricsChartOptions = {}) {
    this.#window = options.window ?? 5000;

    this.#chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: [],
        datasets: [
          {
            label: 'Loss',
            data: [],
            // Red marks loss: it is the series the growth spike must stand out in.
            borderColor: '#dc2626',
            backgroundColor: 'rgba(220, 38, 38, 0.10)',
            borderWidth: 1.5,
            tension: 0.25,
            yAxisID: 'loss',
          },
          {
            label: METRIC_LABELS.accuracy,
            data: [],
            // The metric keeps one blue regardless of which metric is selected, so
            // switching metrics never looks like a different chart.
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.10)',
            borderWidth: 1.5,
            tension: 0.25,
            yAxisID: 'metric',
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        // Both curves are read as lines; a marker per sample only turns the chart
        // into noise, so points are disabled for the whole chart rather than per dataset.
        elements: { point: { radius: 0, hoverRadius: 0 } },
        scales: {
          x: {
            title: { display: true, text: 'Training batch' },
            // Horizontal labels: the pane is narrow and rotated ticks are harder to read.
            ticks: { maxTicksLimit: 10, maxRotation: 0, autoSkipPadding: 12 },
          },
          loss: {
            type: 'linear',
            position: 'left',
            title: { display: true, text: 'Loss' },
            beginAtZero: true,
          },
          metric: {
            type: 'linear',
            position: 'right',
            title: { display: true, text: METRIC_LABELS.accuracy },
            min: 0,
            max: 1,
            grid: { drawOnChartArea: false },
          },
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, pointStyle: 'line', pointStyleWidth: 24 },
          },
          tooltip: { enabled: true },
        },
      },
    });
  }

  get selection(): ChartSelection {
    return { ...this.#selection };
  }

  /** Same code path for control clicks and incoming data, so the two cannot diverge. */
  configure(patch: Partial<ChartSelection>): void {
    this.#selection = { ...this.#selection, ...patch };
    this.#applyTitles();
    this.#render();
  }

  /** Replaces the whole history; the store stays the source of truth. */
  setData(points: readonly MetricPoint[]): void {
    this.#points = points;
    this.#render();
  }

  destroy(): void {
    this.#chart.destroy();
  }

  #applyTitles(): void {
    const x = this.#chart.options.scales?.['x'];
    if (x?.title) {
      x.title.text = this.#selection.xAxis === 'epoch' ? 'Epoch' : 'Training batch';
    }

    const metric = this.#chart.options.scales?.['metric'];
    if (metric?.title) {
      const label = METRIC_LABELS[this.#selection.metric];
      metric.title.text = this.#selection.display === 'raw' ? label : `${label} (smoothed)`;
    }
  }

  #render(): void {
    const series = buildSeries(this.#points, this.#seriesOptions()).slice(-this.#window);
    const lossDataset = this.#chart.data.datasets[0]!;
    const metricDataset = this.#chart.data.datasets[1];

    this.#chart.data.labels = series.map((point) => point.label);
    lossDataset.data = series.map((point) => point.loss);
    if (metricDataset) {
      metricDataset.label = METRIC_LABELS[this.#selection.metric];
      metricDataset.data = series.map((point) => point.value);
    }
    this.#chart.update('none');
  }

  /** The span is expressed in the active x-axis unit, so it stays meaningful in both views. */
  #seriesOptions(): SeriesOptions {
    return {
      xAxis: this.#selection.xAxis,
      metric: this.#selection.metric,
      display: this.#selection.display,
      span: this.#selection.span,
    };
  }
}
